import * as React from "react";
import { Text } from "@uikit";

import { useStaticQuery, graphql, Link } from "gatsby";
import { Coordinate } from "@kalabo/golfsavers-common";
import { calcCrow, slugify } from "@kalabo/golfsavers-common";
import { isRegionalSite } from "@utilities/core";
import StarRating from "@components/starRating";
import { HotelExtended } from "@kalabo/golfsavers-common";
import { Region } from "@kalabo/golfsavers-common";

interface iHotelsProps {
  region: Region;
  center: Coordinate;
  maxDistance?: number;
}

const NearbyHotels = ({ region, center, maxDistance = 10 }: iHotelsProps) => {
  const data = useStaticQuery(graphql`
    query {
      hotels: allGolfsaversHotels {
        edges {
          node {
            id
            name {
              en
            }
            description {
              en
            }
            facilities {
              bar_lounge
              breakfast_buffet
              business_center
              fitness_center
              free_wifi
              indoor_pool
              kids_club
              non_smoking_rooms
              onsite_golf
              outdoor_pool
              pets_allowed
              restaurant
              room_service
              spa
              tennis_courts
            }
            full_address
            hotel_type

            images {
              alt
              url
            }
            is_recommended
            latitude
            longitude
            number_of_rooms
            policies {
              checkin_time
              checkout_time
            }
            published
            region {
              country {
                id
                name {
                  en
                }
                slug
              }
              slug
              id
              name {
                en
              }
            }
            slug
            star_rating
            seo {
              canonical
              description
              keywords
              title
            }
            region_id
            summary {
              en
            }
            hotel_id
          }
        }
      }
    }
  `);

  const hotels = data.hotels.edges
    .map((edge: any) => edge.node as HotelExtended)
    .map((hotel: HotelExtended) => {
      return {
        ...hotel,
        distance: calcCrow(
          center.latitude,
          center.longitude,
          hotel.latitude,
          hotel.longitude
        ),
      };
    })
    .sort((a: HotelExtended, b: HotelExtended) => {
      return a.distance.km - b.distance.km;
    })
    .filter((hotel: HotelExtended) => {
      return (
        hotel.latitude !== center.latitude &&
        hotel.longitude !== center.longitude
      );
    })
    .filter((hotel: HotelExtended) => {
      return hotel.distance.km < maxDistance;
    })
    .slice(0, 3);

  if (hotels.length === 0) return null;

  return (
    <div className="mb-10 flex-col flex flex-1 overflow-hidden">
      <Text
        size={17}
        weight="semibold"
        color="black90"
        element="h3"
        leading={1.3}
        className="mb-4"
      >
        Hotels Nearby
      </Text>
      <div className="flex flex-col gap-2">
        {hotels.map((hotel: HotelExtended, idx: any) => {
          let images = hotel.images;
          let featured_image = images?.length > 0 ? images[0].url : null;
          return (
            <Link
              to={
                isRegionalSite()
                  ? `/${region.slug}-golf-packages/${hotel.slug}`
                  : `/${slugify(region.country.slug)}-golf-packages/${slugify(
                      region.name.en.toLowerCase()
                    )}-golf-holidays/${hotel.slug}`
              }
              key={idx}
              className="group flex gap-[10px] items-center"
            >
              {" "}
              <div
                className={`${"min-w-[70px]"} rounded-[4px] overflow-hidden bg-black `}
              >
                <img
                  className="w-[70px] rounded-[4px] transition-all group-hover:opacity-90"
                  src={`${process.env.GATSBY_IMAGE_PREFIX}${featured_image}?aspect_ratio=1:1&width=70&height=70)`}
                />
              </div>
              <div className="flex flex-col gap-1 overflow-hidden">
                <Text
                  element="span"
                  size={15}
                  color="black90"
                  weight="semibold"
                  leading={1.3}
                  className="group-hover:text-blue group-hover:underline text-ellipsis overflow-hidden w-full whitespace-nowrap"
                >
                  {hotel.name.en}
                </Text>
                <div className="flex items-center gap-1">
                  <StarRating
                    mode="large"
                    hideCount
                    hideEmpty
                    color="green"
                    rating={hotel.star_rating}
                    size="small"
                  />
                </div>
                <div className="flex items-center">
                  <Text size={13} color="black80" leading={1.3}>
                    {hotel.distance.km.toFixed(1)} km
                  </Text>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default NearbyHotels;
